import { Link } from 'react-router-dom';
import {
  StatCard, Card, CardHeader, CardBody, ErrorNotice, Button, StatusBadge,
} from '../../components/ui/index.js';
import { useResource } from '../../hooks/useApi.js';
import { formatMoney, formatNumber, formatPercent, formatTimestamp } from '../../lib/format.js';
import { Shield } from 'lucide-react';
import { PageHero } from '../../components/PageHero.jsx';

/** AD-1: the platform's own overview. Deliberately read-mostly. */
export function AdminOverviewPage() {
  const { data, error, loading, refetch } = useResource('/admin/overview');

  return (
    <>
      <PageHero
        icon={Shield}
        chip="Platform"
        title="Platform overview"
        blurb="Every organisation on the platform, last 30 days. None of this money passed through us — it moved between each business and their own gateway."
        actions={
          <>
            <Link to="/admin/merchants"><Button variant="secondary">Organisations</Button></Link>
            <Link to="/admin/health"><Button variant="secondary">Webhook health</Button></Link>
          </>
        }
      />

      {error ? (
        <ErrorNotice error={error} onRetry={refetch} />
      ) : (
        <>
          <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <StatCard label="Organisations" loading={loading} value={formatNumber(data?.merchants.total)}
              hint={`${formatNumber(data?.merchants.active ?? 0)} active, ${formatNumber(data?.merchants.onboarding ?? 0)} onboarding`} />
            <StatCard label="Payments" loading={loading} value={formatNumber(data?.payments.count)}
              hint={`${formatNumber(data?.payments.succeeded ?? 0)} succeeded`} />
            {/*
              * One tile per currency. The old version summed every currency into
              * one number and rendered it with a dollar sign, so an INR-only
              * platform reported "$201,830" — a figure that is wrong in both
              * currencies. There is no FX rate here and no business inventing one.
              */}
            <StatCard
              label="Volume"
              loading={loading}
              value={
                (data?.payments.volumeByCurrency ?? []).length === 0
                  ? '—'
                  : data.payments.volumeByCurrency
                      .map((v) => formatMoney(v.volume, v.currency))
                      .join('  ·  ')
              }
              hint={
                (data?.payments.volumeByCurrency ?? []).length > 1
                  ? 'Net of refunds, per currency — never added together'
                  : 'Net of refunds'
              }
            />
            {/* NF-5: a drop here is measured in lost money per minute. */}
            <StatCard label="Success rate" loading={loading} value={formatPercent(data?.payments.successRate)}
              tone={data?.payments.successRate != null && data.payments.successRate < 85 ? 'danger' : 'default'}
              hint="Platform-wide" />
          </div>

          <div className="grid gap-6 lg:grid-cols-2">
            <Card>
              <CardHeader
                title="Webhook queue"
                blurb="NF-3: the platform's primary health signal"
                actions={<Link to="/admin/health" className="text-xs font-medium text-accent-ink hover:underline">Details</Link>}
              />
              <CardBody>
                <dl className="grid grid-cols-2 gap-4 sm:grid-cols-4">
                  {[
                    ['Queue depth', data?.webhooks.queueDepth, false],
                    ['Due now', data?.webhooks.dueNow, false],
                    ['Delivering', data?.webhooks.delivering, false],
                    ['Gave up', data?.webhooks.exhausted, true],
                  ].map(([label, value, isBad]) => (
                    <div key={label}>
                      <dt className="text-xs text-muted">{label}</dt>
                      <dd className={`tabular mt-0.5 text-lg font-semibold ${isBad && value > 0 ? 'text-red-600' : 'text-ink'}`}>
                        {formatNumber(value ?? 0)}
                      </dd>
                    </div>
                  ))}
                </dl>
                {data?.webhooks.oldestPendingAt && (
                  <p className="mt-4 border-t border-line pt-3 text-xs text-muted">
                    Oldest undelivered event queued {formatTimestamp(data.webhooks.oldestPendingAt)}.
                  </p>
                )}
              </CardBody>
            </Card>

            <Card>
              <CardHeader
                title="Organisation states"
                actions={<Link to="/admin/merchants" className="text-xs font-medium text-accent-ink hover:underline">View all</Link>}
              />
              <CardBody>
                <ul className="space-y-3">
                  {[
                    ['active', data?.merchants.active],
                    ['onboarding', data?.merchants.onboarding],
                    ['suspended', data?.merchants.suspended],
                  ].map(([status, count]) => (
                    <li key={status} className="flex items-center justify-between">
                      <StatusBadge status={status} size="sm" />
                      <span className="tabular text-sm font-medium text-ink">{formatNumber(count ?? 0)}</span>
                    </li>
                  ))}
                </ul>
              </CardBody>
            </Card>
          </div>
        </>
      )}
    </>
  );
}

export default AdminOverviewPage;
